import React, {useEffect, useState} from 'react';
import {Stack} from '@mui/material';
import {Body, Button, Headline, Label} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import Modal from '@/components/modal';
import StarRating from '@/components/star-rating';
import Textarea from '@/components/textarea';
import styles from './CampaignCloseModal.module.scss';

interface CampaignCloseModalProps {
  open: boolean;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (payload: {rating: number; comment: string | null}) => void;
}

export default function CampaignCloseModal({
  open,
  isSubmitting,
  onCancel,
  onConfirm,
}: CampaignCloseModalProps) {
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');

  // Clear inputs on a fresh open (closed→open) so stale values from a previous
  // attempt don't reappear. Keyed on `open`, so an in-modal retry (open stays
  // true) preserves what the user already entered.
  useEffect(() => {
    if (open) {
      setRating(null);
      setComment('');
    }
  }, [open]);

  const handleConfirm = () => {
    if (rating === null) {
      return;
    }
    onConfirm({rating, comment: comment.trim() || null});
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleConfirm();
  };

  return (
    <Modal
      open={open}
      size="small"
      onClose={isSubmitting ? undefined : onCancel}
      disableBackdropClick={isSubmitting}
      disableEscapeKeyDown={isSubmitting}
      contentClassName={styles.modalContent}
    >
      <form className={styles.modalInner} onSubmit={handleSubmit}>
        <Headline size="md" className={styles.modalTitle}>
          {translate('Close campaign?')}
        </Headline>
        <Body size="md" className={styles.modalBody}>
          {translate(
            'This will complete the campaign. Pending offers will be rescinded and in-progress creators will be moved to complete. This cannot be undone.'
          )}
        </Body>

        <Stack spacing={2} className={styles.formFields}>
          <Stack spacing={1}>
            <Label size="sm">
              {translate('How would you rate this campaign?')}
            </Label>
            <StarRating value={rating} onChange={setRating} />
          </Stack>
          {/*
            Slightly larger label→field gap than the rating group: the star
            glyphs carry ~2px of transparent padding inside their icon box, so
            this keeps the comment box optically aligned with the stars.
          */}
          <Stack spacing={1.25}>
            <Label size="sm">{translate('Comments (optional)')}</Label>
            <Textarea
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder={translate('Tell us more about your experience…')}
              minRows={5}
              maxRows={5}
              className={styles.commentBox}
              testID="campaign-close-feedback-comment"
            />
          </Stack>
        </Stack>

        <div className={styles.modalActions}>
          <Button
            type="button"
            appearance="neutral"
            mode="tinted"
            size="large"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            {translate('Cancel')}
          </Button>
          <Button
            type="button"
            appearance="brand"
            mode="filled"
            size="large"
            isLoading={isSubmitting}
            disabled={isSubmitting || rating === null}
            onClick={handleConfirm}
          >
            {translate('Close campaign')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
